/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { ContentBlock, Heading, Text, Button, Icon, CardCarousel } from 'sarsaparilla';

import { useUserLocation } from 'shared-ui/geolocation';

import { useHolidayStaysQuery } from './api/search-api.queries';
import { get2025HolidayStaysDates } from './utils/get2025HolidayStaysDates';
import { CarouselCard } from './CarouselCard/CarouselCard';
import { RecommendationCarouselSkeleton } from './RecommendationCarouselSkeleton';
import { dateToSillySearchFormat } from './utils';

/**
 * Renders a carousel of campsites available nearby the user's location.
 */
export function HolidayStays() {
    const userLocation = useUserLocation();

    const { data, isError, isPending } = useHolidayStaysQuery({
        isEnabled: userLocation !== null,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        location: userLocation?.location,
    });

    const cards = React.useMemo(() => {
        if (!data) {
            return [];
        }

        return data.map((item) => {
            return (
                <CarouselCard
                    key={item.id}
                    item={item}
                    showAvailDates
                    onClick={() => {}}
                    gaTrackingId="454851019940"
                    gaTrackingValue={item.name}
                />
            );
        });
    }, [data]);

    const exploreMoreHref = React.useMemo(() => {
        if (!userLocation) {
            return '';
        }

        return getExploreMoreHref(userLocation.latitude, userLocation.longitude);
    }, [userLocation]);

    if (isPending) {
        return <RecommendationCarouselSkeleton />;
    }

    // Render nothing is there's nothing to show
    if (isError || !data || data.length <= 4) {
        return null;
    }

    return (
        <section>
            <ContentBlock>
                <Heading headingLevel={2} appearance="h4" hasUnderline="left">
                    Holiday Stays
                </Heading>
                <Text className="mb-2">
                    Memorable holiday escapes to wide-open skies.
                    <Button
                        appearance="link"
                        href={exploreMoreHref}
                        iconAfterElement={<Icon iconName="arrow-forward" />}
                        className="ml-half"
                        style={{ verticalAlign: 'baseline' }}
                        aria-label="Explore more holiday stays"
                        gaTrackingId="211885852549"
                    >
                        Explore More
                    </Button>
                </Text>
            </ContentBlock>

            <CardCarousel
                accessibleName="Holiday Stays"
                gaTrackingId="899958138696"
                overscanPerBreakpoint={{ md: 8 }}
                simulateContentBlock
                peekPerBreakpoint={{ xs: 24, md: 0 }}
            >
                {cards}
            </CardCarousel>
        </section>
    );
}

function getExploreMoreHref(latitude: number, longitude: number): string {
    const [startDate, endDate] = get2025HolidayStaysDates();

    const searchParams = new URLSearchParams({
        inventory_type: 'camping',
        checkin: dateToSillySearchFormat(startDate),
        checkout: dateToSillySearchFormat(endDate),
        sort: 'available',
        lat: latitude.toString(),
        lng: longitude.toString(),
        clear: 'true',
        radius: '8000',
        campsite_type: 'lodging (cabins/yurts/lookouts)',
    });

    return `/search?${searchParams.toString()}`;
}

// cSpell:ignore checkin
